import { createContext, useContext, useEffect, useReducer } from 'react'

import type { RefObject } from 'react'

import type { BuiltInValidatorProps } from './FormBuiltInValidators'
import type { FieldValidator, FormValidator } from './FormValidators'

import { clone } from '../utilities/clone'
import { runFieldValidators, runFormValidators } from './FormValidators'

export type FormChangeHandler<V extends Record<string, any>> = (value: V) => void
export type FormFlagHandler = (flags: Record<string, string[]>, showErrors: boolean) => void
export type FormSubmitHandler = () => void

export type FormState = {
  belongsToForm: boolean
  hasAttemptedSubmit: boolean
  value: Record<string, any>
  providedFlags: Record<string, string[]>
  erroringFormFlags: Record<string, string[]>
  erroringFieldFlags: Record<string, Record<string, number>>
  erroredFormFlags: Record<string, string[]>
  erroredFieldFlags: Record<string, string[]>
  formFlags: Record<string, Set<string>>
  fieldFlags: Record<string, Set<string>>
  showErrorFlagsAfterSubmit: boolean
  formChangeHandlerCall: Parameters<FormChangeHandler<any>> | null
  formFlagHandlerCall: Parameters<FormFlagHandler> | null
  formSubmitHandlerCall: Parameters<FormSubmitHandler> | null
}

const defaultInitialState: FormState = {
  belongsToForm: false,
  hasAttemptedSubmit: false,
  value: {},
  providedFlags: {},
  erroringFormFlags: {},
  erroringFieldFlags: {},
  erroredFormFlags: {},
  erroredFieldFlags: {},
  formFlags: {},
  fieldFlags: {},
  showErrorFlagsAfterSubmit: false,
  formChangeHandlerCall: null,
  formFlagHandlerCall: null,
  formSubmitHandlerCall: null,
}

export type FormActionSetFormValue = {
  type: 'setFormValue'
  value: Record<string, any>
}

export type FormActionSetFieldValue = {
  type: 'setFieldValue'
  name: string
  value: any
}

export type FormActionRunFormValidators = {
  type: 'runFormValidator'
  formValidators: FormValidator<any>[]
}

export type FormActionRunFieldValidators = {
  type: 'runFieldValidator'
  name: string
  validatorProps: BuiltInValidatorProps<any>
  fieldValidators?: FieldValidator<any>[]
  erroringFlags?: string[]
}

export type FormActionSetErroredFieldFlags = {
  type: 'setErroringFieldFlags'
  name: string
  flags: string[]
}

export type FormActionUnsetErroredFieldFlags = {
  type: 'unsetErroringFieldFlags'
  name: string
  flags: string[]
}

export type FormActionMaybeCallFlagHandler = {
  type: 'maybeCallFlagHandler'
}

export type FormActionTrySubmit = {
  type: 'trySubmit'
}

export type FormActionClearSubmit = {
  type: 'clearSubmit'
}

export type FormActionSetProvidedFlags = {
  type: 'setProvidedFlags'
  providedFlags: Record<string, string[]>
}

export type FormActionSetErroringFormFlags = {
  type: 'setErroringFormFlags'
  erroringFormFlags: Record<string, string[]>
}

export type FormAction =
  | FormActionSetFormValue
  | FormActionSetFieldValue
  | FormActionRunFormValidators
  | FormActionRunFieldValidators
  | FormActionSetErroredFieldFlags
  | FormActionUnsetErroredFieldFlags
  | FormActionMaybeCallFlagHandler
  | FormActionTrySubmit
  | FormActionClearSubmit
  | FormActionSetProvidedFlags
  | FormActionSetErroringFormFlags

const formReducer = (state: FormState, action: FormAction) => {
  const updatedState = { ...state }

  if (action.type === 'setFormValue') {
    updatedState.value = action.value
  }

  if (action.type === 'setFieldValue') {
    const updatedValue = { ...state.value, [action.name]: action.value }
    updatedState.value = updatedValue
    updatedState.formChangeHandlerCall = [updatedValue]
  }

  if (action.type === 'runFormValidator') {
    updatedState.formFlags = runFormValidators(action.formValidators, state.value)

    for (const [fieldName, fieldFlags] of Object.entries(updatedState.formFlags)) {
      const erroringFormFlags = [
        ...(state.erroringFormFlags[fieldName] ?? []),
        ...Object.keys(state.erroringFieldFlags[fieldName] ?? {}),
      ]
      updatedState.erroredFormFlags[fieldName] = erroringFormFlags.filter((f) => fieldFlags.has(f))
    }
  }

  if (action.type === 'runFieldValidator') {
    const fieldFlags = runFieldValidators(
      action.validatorProps,
      action.fieldValidators,
      state.value[action.name],
    )

    const erroringFormFlags = [
      ...(state.erroringFormFlags[action.name] ?? []),
      ...Object.keys(state.erroringFieldFlags[action.name] ?? {}),
    ]

    updatedState.fieldFlags[action.name] = fieldFlags
    updatedState.erroredFieldFlags[action.name] = erroringFormFlags.filter((f) => fieldFlags.has(f))
  }

  if (action.type === 'setErroringFieldFlags') {
    for (const flag of action.flags) {
      updatedState.erroringFieldFlags = {
        ...updatedState.erroringFieldFlags,
        [action.name]: {
          ...(updatedState.erroringFieldFlags[action.name] ?? {}),
          [flag]: (updatedState.erroringFieldFlags[action.name]?.[flag] ?? 0) + 1,
        },
      }
    }
  }

  if (action.type === 'unsetErroringFieldFlags') {
    for (const flag of action.flags) {
      updatedState.erroringFieldFlags = {
        ...updatedState.erroringFieldFlags,
        [action.name]: {
          ...(updatedState.erroringFieldFlags[action.name] ?? {}),
          [flag]: (updatedState.erroringFieldFlags[action.name]?.[flag] ?? 0) - 1,
        },
      }
    }
  }

  if (action.type === 'maybeCallFlagHandler') {
    const canShowErrored = state.hasAttemptedSubmit || !state.showErrorFlagsAfterSubmit
    const flags = {
      ...Object.fromEntries(Object.entries(state.formFlags).map(([n, f]) => [n, [...f]])),
      ...Object.fromEntries(Object.entries(state.fieldFlags).map(([n, f]) => [n, [...f]])),
    }
    updatedState.formFlagHandlerCall = [flags, canShowErrored]
  }

  if (action.type === 'trySubmit') {
    // Stay true after a submit attempt so Form `flags` (API issues) can show on FieldError
    // after async handlers run. Client-only validation still gates formSubmitHandlerCall.
    updatedState.hasAttemptedSubmit = true

    const noFieldErrors = Object.values(state.erroredFieldFlags).every((f) => f.length === 0)
    const noFormErrors = Object.values(state.erroredFormFlags).every((f) => f.length === 0)

    if (noFieldErrors && noFormErrors) {
      updatedState.formSubmitHandlerCall = []
    }
  }

  if (action.type === 'setProvidedFlags') {
    updatedState.providedFlags = action.providedFlags
  }

  if (action.type === 'setErroringFormFlags') {
    updatedState.erroringFormFlags = action.erroringFormFlags
  }

  if (action.type === 'clearSubmit') {
    updatedState.formSubmitHandlerCall = null
  }

  return updatedState
}

type CallRequestProps = 'formChangeHandlerCall' | 'formFlagHandlerCall' | 'formSubmitHandlerCall'
type HandlerProps = {
  formChangeHandlerRef: RefObject<FormChangeHandler<any> | undefined>
  formFlagHandlerRef: RefObject<FormFlagHandler | undefined>
  formSubmitHandlerRef: RefObject<FormSubmitHandler | undefined>
}

export type UseFormReducerState = Partial<Omit<FormState, CallRequestProps>> & HandlerProps

export const useFormReducer = (state?: UseFormReducerState) => {
  const initialState = clone(defaultInitialState)
  if (state) {
    for (const [key, value] of Object.entries(state)) {
      if (value !== undefined) initialState[key as keyof FormState] = value as any
    }
  }

  const reducer = useReducer(formReducer, initialState)
  const formState = reducer[0]
  const dispatch = reducer[1]

  const updateValueEffect = () => {
    if (!state?.value) return
    dispatch({ type: 'setFormValue', value: state?.value })
  }

  const updateExternalFlagsEffect = () => {
    if (state?.providedFlags !== undefined) {
      dispatch({ type: 'setProvidedFlags', providedFlags: state.providedFlags })
    }
    if (state?.erroringFormFlags !== undefined) {
      dispatch({ type: 'setErroringFormFlags', erroringFormFlags: state.erroringFormFlags })
    }
  }

  const callChangeEffect = () => {
    if (!formState.formChangeHandlerCall) return
    if (!state?.formChangeHandlerRef.current) return
    state?.formChangeHandlerRef.current(...formState.formChangeHandlerCall)
  }

  const callFlagEffect = () => {
    if (!formState.formFlagHandlerCall) return
    if (!state?.formFlagHandlerRef.current) return
    state?.formFlagHandlerRef.current(...formState.formFlagHandlerCall)
  }

  const callSubmitEffect = () => {
    if (!formState.formSubmitHandlerCall) return
    if (!state?.formSubmitHandlerRef.current) return
    state?.formSubmitHandlerRef.current()
    dispatch({ type: 'clearSubmit' })
  }

  useEffect(updateValueEffect, [dispatch, state?.value])
  useEffect(updateExternalFlagsEffect, [dispatch, state?.providedFlags, state?.erroringFormFlags])
  useEffect(callChangeEffect, [formState.formChangeHandlerCall, state?.formChangeHandlerRef])
  useEffect(callFlagEffect, [formState.formFlagHandlerCall, state?.formFlagHandlerRef])
  useEffect(callSubmitEffect, [formState.formSubmitHandlerCall, state?.formSubmitHandlerRef])

  return reducer
}

export const FormContext = createContext<ReturnType<typeof useFormReducer> | null>(null)

export const useFormContext = (state?: UseFormReducerState) => {
  let reducer = useContext(FormContext)
  if (!reducer) {
    // Intentionally calling hook conditionally - this is a fallback pattern
    reducer = useFormReducer(state)
  }
  return reducer
}
